/**
 * Life Annotations Store — per-year stage + custom events.
 * Persisted to localStorage. Source of truth for calendar colors and timeline.
 *
 * Stage presets: maktab, kollej, universitet, ish, armiya, kasallik, sayohat, oila, nafaqa, boshqa
 * Events: max 3 per year, each has emoji + name.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// ── Stage presets ──

export interface StagePreset {
  id: string
  icon: string
  color: string
}

export const STAGE_PRESETS: StagePreset[] = [
  { id: 'bolalik', icon: '👶', color: '#52DAFF' },
  { id: 'maktab', icon: '📚', color: '#22C55E' },
  { id: 'kollej', icon: '🏫', color: '#10B981' },
  { id: 'universitet', icon: '🎓', color: '#A855F7' },
  { id: 'magistratura', icon: '📖', color: '#8B5CF6' },
  { id: 'armiya', icon: '🎖️', color: '#6B7280' },
  { id: 'ish', icon: '💼', color: '#0061FF' },
  { id: 'biznes', icon: '🏢', color: '#0EA5E9' },
  { id: 'frilanser', icon: '💻', color: '#06B6D4' },
  { id: 'oila', icon: '👨‍👩‍👧', color: '#EC4899' },
  { id: 'sayohat', icon: '✈️', color: '#F97316' },
  { id: 'kasallik', icon: '🏥', color: '#EF4444' },
  { id: 'dam_olish', icon: '🌴', color: '#84CC16' },
  { id: 'nafaqa', icon: '🌅', color: '#F59E0B' },
  { id: 'boshqa', icon: '📌', color: '#64748B' },
]

// ── Types ──

export interface YearEvent {
  emoji: string
  name: string
}

export interface YearAnnotation {
  stageId: string | null
  events: YearEvent[]  // max 3
}

interface LifeAnnotationsState {
  annotations: Record<number, YearAnnotation>  // key = calendar year
  editingYear: number | null

  setYearStage: (year: number, stageId: string | null) => void
  addYearEvent: (year: number, event: YearEvent) => void
  updateYearEvent: (year: number, index: number, event: YearEvent) => void
  removeYearEvent: (year: number, index: number) => void
  setEditingYear: (year: number | null) => void
  getAnnotation: (year: number) => YearAnnotation
}

const EMPTY_ANNOTATION: YearAnnotation = { stageId: null, events: [] }

export const useLifeAnnotationsStore = create<LifeAnnotationsState>()(
  persist(
    (set, get) => ({
      annotations: {},
      editingYear: null,

      setYearStage: (year, stageId) => set((s) => ({
        annotations: {
          ...s.annotations,
          [year]: { ...(s.annotations[year] ?? EMPTY_ANNOTATION), stageId },
        },
      })),

      addYearEvent: (year, event) => set((s) => {
        const existing = s.annotations[year] ?? EMPTY_ANNOTATION
        if (existing.events.length >= 3) return s
        return {
          annotations: {
            ...s.annotations,
            [year]: { ...existing, events: [...existing.events, event] },
          },
        }
      }),

      updateYearEvent: (year, index, event) => set((s) => {
        const existing = s.annotations[year] ?? EMPTY_ANNOTATION
        const events = [...existing.events]
        events[index] = event
        return {
          annotations: {
            ...s.annotations,
            [year]: { ...existing, events },
          },
        }
      }),

      removeYearEvent: (year, index) => set((s) => {
        const existing = s.annotations[year] ?? EMPTY_ANNOTATION
        return {
          annotations: {
            ...s.annotations,
            [year]: { ...existing, events: existing.events.filter((_, i) => i !== index) },
          },
        }
      }),

      setEditingYear: (year) => set({ editingYear: year }),

      getAnnotation: (year) => get().annotations[year] ?? EMPTY_ANNOTATION,
    }),
    {
      name: 'life-annotations',
      partialize: (s) => ({ annotations: s.annotations }),
    },
  ),
)

// ── Helpers ──

export function getPresetById(id: string): StagePreset | undefined {
  return STAGE_PRESETS.find((p) => p.id === id)
}

export function getStageColorForYear(
  year: number,
  annotations: Record<number, YearAnnotation>,
): string | null {
  const ann = annotations[year]
  if (!ann?.stageId) return null
  return getPresetById(ann.stageId)?.color ?? null
}
